import { computed, nextTick, shallowRef } from 'vue'

import type { ComputedRef, Ref, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppSelectSearchableProps } from '../types/interfaces'
import type { SelectionAdapter, UseAppSelectCoreReturn } from '../../app-select'

import {
  useAppSelectCore,
  useSelectSearch,
  useSelectTriggerTooltip,
  createSingleSelectionAdapter,
} from '../../app-select'

interface AppSelectSearchableOptions<TValue = string | number> {
  modelValue: Ref<TValue | undefined>
  props: AppSelectSearchableProps<TValue>
  onOpen: () => void
  onClose: () => void
  onSearch: (query: string) => void
  onLoadMore: (query: string) => void
}

interface AppSelectSearchableReturn<TValue = string | number>
  extends UseAppSelectCoreReturn<TValue> {
  displayedOptions: ComputedRef<AppSelectOption<TValue>[]>
  inputValue: WritableComputedRef<string>
  inputPlaceholder: ComputedRef<string>
  tooltipText: ComputedRef<string>
  showClearButton: ComputedRef<boolean>
  showNoResults: ComputedRef<boolean>
  isClearDisabled: ComputedRef<boolean>
  isLoadMoreDisabled: ComputedRef<boolean>
  handleInputFocus: () => void
  handleInputBlur: () => void
  handleClearClick: () => void
  handleLoadMoreClick: () => void
}

export const useAppSelectSearchable = <TValue = string | number>(
  options: AppSelectSearchableOptions<TValue>,
): AppSelectSearchableReturn<TValue> => {
  const { modelValue, props, onOpen, onClose, onSearch, onLoadMore } = options

  // ── Shared search engine (query, displayedOptions, flags, debounce) ──
  const {
    query,
    isFocused,
    displayedOptions,
    showClearButton,
    showNoResults,
    isClearDisabled,
    isLoadMoreDisabled,
    handleInputBlur,
    handleLoadMoreClick,
    resetQuery,
  } = useSelectSearch<TValue>({ props, onSearch, onLoadMore })

  // ── Selected-option resolution (single, with off-page fallback) ──
  const internalSelectedOption = computed<AppSelectOption<TValue> | undefined>(
    () => props.options.find(o => o.value === modelValue.value),
  )

  const lastPickedOption = shallowRef<AppSelectOption<TValue>>()

  const resolvedSelectedOption = computed<AppSelectOption<TValue> | undefined>(() => {
    if (internalSelectedOption.value) return internalSelectedOption.value
    if (props.selectedOption && props.selectedOption.value === modelValue.value) {
      return props.selectedOption
    }
    if (lastPickedOption.value && lastPickedOption.value.value === modelValue.value) {
      return lastPickedOption.value
    }
    return undefined
  })

  const singleSelection = createSingleSelectionAdapter<TValue>({ modelValue, displayedOptions })

  const selection: SelectionAdapter<TValue> = {
    ...singleSelection,
    toggleSelection: (option) => {
      lastPickedOption.value = option
      singleSelection.toggleSelection(option)
    },
  }

  const core = useAppSelectCore<TValue>({
    props,
    displayedOptions,
    mode: { inputEditable: true },
    selection,
    onOpen,
    onClose: () => {
      resetQuery()
      onClose()
    },
  })

  // ── Input bridge ────────────────────────────────────
  const inputValue: WritableComputedRef<string> = computed({
    get: () => {
      if (isFocused.value || query.value !== '') return query.value
      return resolvedSelectedOption.value?.text ?? ''
    },
    set: (value: string) => {
      query.value = value
    },
  })

  const inputPlaceholder = computed<string>(() => {
    if (isFocused.value) return ''
    if (resolvedSelectedOption.value) return ''
    return props.placeholder ?? ''
  })

  // ── Overflow tooltip ────────────────────────────────
  const selectedText = computed<string>(() => resolvedSelectedOption.value?.text ?? '')

  const { tooltipText } = useSelectTriggerTooltip({
    inputRef: core.inputRef,
    text: selectedText,
    isOpen: core.isOpen,
  })

  // ── Core-coupled handlers (need core.inputRef, so they live here) ──
  const handleInputFocus = (): void => {
    isFocused.value = true
    core.handleInputFocus()
  }

  const handleClearClick = (): void => {
    if (isClearDisabled.value) return
    query.value = ''
    void nextTick(() => core.inputRef.value?.focus())
  }

  return {
    ...core,
    displayedOptions,
    inputValue,
    inputPlaceholder,
    tooltipText,
    showClearButton,
    showNoResults,
    isClearDisabled,
    isLoadMoreDisabled,
    handleInputFocus,
    handleInputBlur,
    handleClearClick,
    handleLoadMoreClick,
  }
}
